import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Panel, Loader, Table, InputNumber } from "rsuite";
import axios from "../../../utilities/axios";
import { ChannelSelectData } from "../../../core/constants/Channels";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { MethodsUtils } from "../../../core/utils/MethodsUtils";
import _ from "lodash";
dayjs.extend(utc)

const { Column, HeaderCell, Cell } = Table

type Props = {
    channels: string[];
    periods: any;
    searchTrigger: number;
}

interface SortData {
    sortColumn?: string;
    sortType?: 'asc' | 'desc';
}

const StoreChannelMonthlyComparisonPanel: React.FC<Props> = ({ channels, periods, searchTrigger }) => {
    const [rows, setRows] = useState<any[]>([])
    const [months, setMonths] = useState<any[]>([])
    const [selectMonth, setSelectMonth] = useState<Date | null>(dayjs().startOf('month').toDate())
    const [loading, setLoading] = useState(false)
    const [sortData, setSortData] = useState<SortData>({})
    const [monthsToSearch, setMonthsToSearch] = useState<number>(6)

    const updateTable = () => {
        setLoading(true)
        setTimeout(() => setLoading(false), 100)
    }

    const fetchData = useMemo(() => _.debounce(async (channels, periods, monthsToSearch) => {
        setLoading(true)
        try {
            const { data } = await axios.post('/api/v1/analytics/sales/store_channel_monthly_comparison', { channels, periods, months: monthsToSearch })
            const reversedMonths = Array.isArray(data.months) ? data.months.reverse() : []
            setRows(Array.isArray(data.data) ? data.data : [])
            setMonths(reversedMonths)
            if (reversedMonths.length > 0) {
                setSelectMonth(reversedMonths[0])
            }
        } catch (err) {
            console.error(err)
            setRows([])
            setMonths([])
        } finally {
            setLoading(false)
        }
    }, 1000), []);

    useEffect(() => {
        if (searchTrigger > 0) {
            fetchData(channels, periods, monthsToSearch);
        }
    }, [searchTrigger, channels, periods, monthsToSearch]);

    const handleSortColumn = (sortColumn: string, sortType?: 'asc' | 'desc') => {
        setSortData({ sortColumn, sortType })
    }

    const monthData = useMemo(() => selectMonth ? rows.find(row => dayjs(row.month).utc().isSame(dayjs(selectMonth).utc(), 'month'))?.data || [] : [], [selectMonth, rows])
    const sortedData = useMemo(() => {
        const { sortColumn, sortType } = sortData
        if (!sortColumn || !sortType) return monthData

        return [...monthData].sort((a, b) => {
            const aVal = a[sortColumn]
            const bVal = b[sortColumn]
            if (typeof aVal === 'string') {
                return sortType === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
            }
            return sortType === 'asc' ? aVal - bVal : bVal - aVal
        })
    }, [monthData, sortData])

    return (
        <Panel
            onEnter={updateTable}
            bordered
            header={
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
                    <strong>Comparativo Mensal Loja x Canal</strong>
                    <div onClick={(e) => e.stopPropagation()}>
                        <InputNumber
                            value={monthsToSearch}
                            onChange={(v) => setMonthsToSearch(+v)}
                            min={6}
                            max={12}
                            step={1}
                            size="sm"
                            prefix="Últimos"
                            postfix="Meses"
                            style={{ width: 200 }}
                        />
                    </div>
                </div>
            }
            style={{ background: 'white', borderColor: 'rgb(255, 140, 66)' }}
            collapsible
        >
            {loading ? (
                <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><Loader content="Carregando..." /></div>
            ) : sortedData.length === 0 ? (
                <div style={{ padding: 16 }}>Nenhum dado</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                        {months.map((month) => (
                            <div
                                key={month}
                                style={{
                                    fontWeight: 'bold',
                                    width: '100%',
                                    borderRadius: 4,
                                    backgroundColor: selectMonth && dayjs(month).utc().isSame(selectMonth, 'month') ? 'rgba(255, 140, 66, 0.2)' : 'transparent',
                                    border: selectMonth && dayjs(month).utc().isSame(selectMonth, 'month') ? '1px solid #ff8c42' : '1px solid #ccc',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    textAlign: 'center'
                                }}
                                onClick={() => setSelectMonth(month)}
                            >
                                {dayjs(month).utc().format('MM/YYYY')}
                            </div>
                        ))}
                    </div>
                    <Table key={`${selectMonth}-${sortedData.length}`} data={sortedData} sortColumn={sortData.sortColumn} sortType={sortData.sortType} onSortColumn={handleSortColumn} autoHeight>
                        <Column width={250} sortable resizable>
                            <HeaderCell>LOJA</HeaderCell>
                            <Cell dataKey="store" />
                        </Column>

                        {ChannelSelectData.map((col) => (
                            <Column key={col.value} width={150} sortable>
                                <HeaderCell>{col.label}</HeaderCell>
                                <Cell dataKey={col.value}>
                                    {(rowData: any) => MethodsUtils.formatCurrency(rowData[col.value])}
                                </Cell>
                            </Column>
                        ))}

                        <Column width={150} sortable>
                            <HeaderCell>TOTAL</HeaderCell>
                            <Cell dataKey="total">
                                {(rowData: any) => MethodsUtils.formatCurrency(rowData.total)}
                            </Cell>
                        </Column>
                    </Table>
                </div>
            )}
        </Panel>
    )
}

export default StoreChannelMonthlyComparisonPanel
